import React, { useState } from 'react';
import PageLayout from '../components/PageLayout';

// Hooks
import { useUserManagement } from '../hooks/useUserManagement';
import { useUserActions } from '../hooks/useUserActions';

// Components
import UsersTable from '../components/users/UsersTable';
import UserFilters from '../components/users/UserFilters';

// Modals
import AddUserModal from '../components/users/modals/AddUserModal';
import EditUserModal from '../components/users/modals/EditUserModal';
import EmailUserModal from '../components/users/modals/EmailUserModal';
import UserDomainsModal from '../components/users/modals/UserDomainsModal';

/**
 * ManagementUsers Container Component
 * Orchestrates fetching, actions, and UI rendering for user management.
 */
const ManagementUsers = () => {
  // Logic Hooks
  const {
    users,
    loading,
    searchTerm,
    setSearchTerm,
    pagination,
    stats,
    handleFilterClick,
    handlePageChange,
    refreshData
  } = useUserManagement();

  const {
    handleCreateUser,
    handleUpdateUser,
    handleDeleteUser,
    handleSendEmail
  } = useUserActions(refreshData); // Auto-refresh on success

  // Modal States
  const [activeModal, setActiveModal] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);

  // Modal Handlers
  const openModal = (modalName, user = null) => {
    setSelectedUser(user);
    setActiveModal(modalName);
  };

  const closeModal = () => {
    setActiveModal(null);
    setSelectedUser(null);
  };

  if (loading && users.length === 0) {
    return (
      <PageLayout title="User Management">
        <div className="d-flex justify-content-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout title="User Management">
      <div className="management-users">
        {/* Filters and Header */}
        <UserFilters
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          onFilterClick={handleFilterClick}
          activeUsersCount={stats.active_users}
          premiumUsersCount={stats.premium_users}
        />

        {/* Users Table */}
        <UsersTable
          users={users}
          pagination={pagination}
          onPageChange={handlePageChange}
          onEdit={(user) => openModal('edit', user)}
          onDelete={handleDeleteUser}
          onEmail={(user) => openModal('email', user)}
          onViewDomains={(user) => openModal('domains', { userId: user.id, email: user.email })} // Passing email as pseudo-user object
        />
      </div>

      {/* Modals */}
      <AddUserModal
        show={activeModal === 'add'}
        onClose={closeModal}
        onSave={handleCreateUser}
      />

      <EditUserModal
        show={activeModal === 'edit'}
        onClose={closeModal}
        onSave={handleUpdateUser}
        user={selectedUser}
      />

      <EmailUserModal
        show={activeModal === 'email'}
        onClose={closeModal}
        onSend={handleSendEmail}
        user={selectedUser}
      />

      <UserDomainsModal
        show={activeModal === 'domains'}
        onClose={closeModal}
        userId={selectedUser?.userId}
        email={selectedUser?.email}
      />

      {/* Floating Action Button for Add User (could also be in Filters) */}
      <div className="position-fixed bottom-0 end-0 p-4 z-[1000]">
        <button
          className="btn btn-primary rounded-circle shadow-lg p-3 w-[60px] h-[60px]"
          onClick={() => openModal('add')}
          title="Add New User"
        >
          <i className="las la-plus fa-2x"></i>
        </button>
      </div>

    </PageLayout>
  );
};

export default ManagementUsers;